import type { GridFilterModel, GridFilterItem } from '@mui/x-data-grid-pro';
import type { MetadataFilterValue } from '@/api/generatedApi';

const MIN_WILDCARD_FILTER_LENGTH = 2;
const WILDCARD_OPERATORS = new Set(['contains', 'startsWith', 'endsWith']);
const STRING_FIELDS = ['name', 'creator'] as const;
const STATUS_FIELD = 'state';
const EXTENSION_FIELD = 'extension';

type FilterValue = string | number | boolean;

interface StringFilter {
  operator: 'eq' | 'contains' | 'startsWith' | 'endsWith' | 'in';
  value: string | string[];
}

interface DateFilter {
  operator: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  value: string;
}

interface MetadataFilter {
  operator: 'eq' | 'contains' | 'startsWith' | 'endsWith' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in';
  value: MetadataFilterValue;
}

export interface SerializedFilters {
  name?: StringFilter[];
  createdAt?: DateFilter[];
  creator?: StringFilter[];
  state?: StringFilter[];
  extension?: StringFilter[];
  metadata?: Record<string, MetadataFilter[]>;
  logicOperator?: 'and' | 'or';
}

const STRING_OPERATOR_MAP: Record<string, StringFilter['operator']> = {
  equals: 'eq',
  contains: 'contains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  is: 'eq',
};

const DATE_OPERATOR_MAP: Record<string, DateFilter['operator']> = {
  is: 'eq',
  after: 'gt',
  onOrAfter: 'gte',
  before: 'lt',
  onOrBefore: 'lte',
};

const NUMERIC_OPERATOR_MAP: Record<string, MetadataFilter['operator']> = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
};

function isWildcardTooShort(operator: string, value: unknown): boolean {
  return WILDCARD_OPERATORS.has(operator) && typeof value === 'string' && value.length < MIN_WILDCARD_FILTER_LENGTH;
}

function serializeFilterItem(
  item: GridFilterItem
): { field: string; filter: StringFilter | DateFilter | MetadataFilter } | undefined {
  if ((item.value === undefined || item.value === '') && item.value !== 0) return undefined;
  if (!item.operator) return undefined;

  const { field, operator, value } = item;

  if (STRING_FIELDS.includes(field as (typeof STRING_FIELDS)[number])) {
    const mappedOperator = STRING_OPERATOR_MAP[operator];
    if (!mappedOperator || typeof value !== 'string' || isWildcardTooShort(mappedOperator, value)) return undefined;
    return { field, filter: { operator: mappedOperator, value } };
  }

  if (field === 'createdAt') {
    const mappedOperator = DATE_OPERATOR_MAP[operator];
    if (!mappedOperator) return undefined;
    return {
      field,
      filter: { operator: mappedOperator, value: value instanceof Date ? value.toISOString() : String(value) },
    };
  }

  if (field === STATUS_FIELD) {
    if (operator === 'isAnyOf' && Array.isArray(value)) {
      return { field, filter: { operator: 'in', value } };
    }
    if (operator === 'equals' && typeof value === 'string') {
      return { field, filter: { operator: 'eq', value } };
    }
    return undefined;
  }

  if (field === EXTENSION_FIELD) {
    if (typeof value !== 'string') return undefined;
    const normalizedValue = value.startsWith('.') ? value.slice(1) : value;
    if (normalizedValue.length < MIN_WILDCARD_FILTER_LENGTH) return undefined;
    return { field, filter: { operator: 'endsWith', value: `.${normalizedValue}` } };
  }

  if (field.startsWith('metadata.')) {
    const mappedOperator = STRING_OPERATOR_MAP[operator] ?? NUMERIC_OPERATOR_MAP[operator];
    if (!mappedOperator || isWildcardTooShort(mappedOperator, value)) return undefined;
    return { field, filter: { operator: mappedOperator, value } };
  }

  return undefined;
}

function getOrFiltersToMerge(filterModel: GridFilterModel): Map<string, FilterValue[]> {
  const result = new Map<string, FilterValue[]>();
  if (filterModel.logicOperator !== 'or') return result;

  const equalityItems = filterModel.items.filter(
    item => (item.operator === 'equals' || item.operator === '=') && item.value !== undefined && item.value !== ''
  );

  const fieldCounts = new Map<string, number>();
  for (const item of equalityItems) {
    fieldCounts.set(item.field, (fieldCounts.get(item.field) ?? 0) + 1);
  }

  for (const [field, count] of fieldCounts) {
    if (count > 1) {
      const values = equalityItems.filter(item => item.field === field).map(item => item.value as FilterValue);
      result.set(field, values);
    }
  }

  return result;
}

function addFilter(
  result: SerializedFilters,
  metadata: Record<string, MetadataFilter[]>,
  field: string,
  filter: StringFilter | DateFilter | MetadataFilter
): void {
  switch (field) {
    case 'name': {
      result.name ??= [];
      result.name.push(filter as StringFilter);
      break;
    }
    case 'createdAt': {
      result.createdAt ??= [];
      result.createdAt.push(filter as DateFilter);
      break;
    }
    case 'creator': {
      result.creator ??= [];
      result.creator.push(filter as StringFilter);
      break;
    }
    case STATUS_FIELD: {
      result.state ??= [];
      result.state.push(filter as StringFilter);
      break;
    }
    case EXTENSION_FIELD: {
      result.extension ??= [];
      result.extension.push(filter as StringFilter);
      break;
    }
    default: {
      if (field.startsWith('metadata.')) {
        const metadataFieldName = field.slice('metadata.'.length);
        metadata[metadataFieldName] ??= [];
        metadata[metadataFieldName].push(filter as MetadataFilter);
      }
    }
  }
}

export function serializeFilterModel(filterModel: GridFilterModel | undefined): SerializedFilters | undefined {
  if (!filterModel?.items || filterModel.items.length === 0) return undefined;

  const result: SerializedFilters = {};
  const metadata: Record<string, MetadataFilter[]> = {};
  const orMerges = getOrFiltersToMerge(filterModel);
  const processedFields = new Set<string>();

  for (const item of filterModel.items) {
    const serialized = serializeFilterItem(item);
    if (!serialized) continue;

    const { field, filter } = serialized;
    if (processedFields.has(field)) continue;

    const mergeValues = orMerges.get(item.field);
    if (mergeValues) {
      processedFields.add(field);
      addFilter(result, metadata, field, { operator: 'in', value: mergeValues } as StringFilter | MetadataFilter);
    } else {
      addFilter(result, metadata, field, filter);
    }
  }

  if (Object.keys(metadata).length > 0) {
    result.metadata = metadata;
  }

  if (filterModel.logicOperator === 'or') {
    result.logicOperator = 'or';
  }

  const hasFilters =
    result.name || result.createdAt || result.creator || result.state || result.extension || result.metadata;
  return hasFilters ? result : undefined;
}
